import React from 'react'
import { Skeleton } from '@mantine/core'

export interface DataTableColumn<T = any> {
  /** Plain text in almost every case; a node when the header carries a hint. */
  header: React.ReactNode
  accessor?: keyof T | ((row: T) => React.ReactNode)
  render?: (row: T, index: number) => React.ReactNode
  className?: string
  minWidth?: string
}

export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[]
  data: T[]
  gridCols?: string
  className?: string
  isLoading?: boolean
  skeletonRows?: number
  title?: string
  headerControls?: React.ReactNode
}

/**
 * The dashboard table.
 *
 * A CSS grid rather than a `<table>`, so every column can be given an `fr`
 * weight and a minimum: all-`auto` tracks distributed the spare width by content
 * length, which left short columns wide and long ones cramped. Callers pass
 * `gridCols` as a `minmax(px,fr)` list weighted by how much room each value
 * actually wants.
 *
 * On its own this still scrolls sideways below `md`. Screens that need a phone
 * layout should reach for ResponsiveTable, which pairs this with a card list.
 */
export function DataTable<T = any>({
  columns,
  data,
  gridCols,
  className = '',
  isLoading = false,
  skeletonRows = 6,
  title,
  headerControls,
}: DataTableProps<T>) {
  const defaultGridCols = `grid-cols-[repeat(${columns.length},1fr)]`

  // `gridCols` is a Tailwind class, not a CSS track list, because it is
  // concatenated into className. Passing raw CSS ("minmax(120px,1fr) …")
  // produced no valid class at all, so the grid silently fell back to a single
  // column and every cell stacked — a wrong table that looked like a layout bug
  // rather than a bad prop. A raw track list is accepted and applied as a style
  // instead, so the mistake renders correctly either way.
  const looksLikeClass = !gridCols || gridCols.trim().startsWith('grid-cols-')
  const gridColumns = looksLikeClass ? gridCols || defaultGridCols : ''
  const gridStyle = looksLikeClass ? undefined : { gridTemplateColumns: gridCols }

  return (
    <div className={`bg-white w-full ${className}`}>
      <div className="overflow-x-auto">
        <div className="min-w-[700px] lg:min-w-0">
          {(title || headerControls) && (
            <div className="px-5 pt-5 pb-4 bg-white flex items-center justify-between gap-4">
              <div className="text-gray-900 text-sm font-semibold leading-7">{title}</div>
              <div>{headerControls}</div>
            </div>
          )}
          {/* Cell padding is deliberately tight (10/12px): with nine narrow
              columns, the two paddings either side of a boundary are what
              separates a value from its neighbour, and 16px each read as a gap
              rather than as breathing room. */}
          <div className={`grid ${gridColumns}`} style={gridStyle}>
            {columns.map((column, index) => (
              <div
                key={index}
                className={`h-11 px-2.5 lg:px-3 py-3 bg-white border-b border-gray-200 flex items-center min-w-0 ${column.className || ''}`}
                style={column.minWidth ? { minWidth: column.minWidth } : undefined}
              >
                <div className="text-dashboard-faint text-xs font-medium leading-tight">
                  {column.header}
                </div>
              </div>
            ))}

            {isLoading
              ? [...Array(skeletonRows)].map((_, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <div
                        key={colIndex}
                        className={`h-16 px-2.5 lg:px-3 py-3 border-b border-gray-200 flex items-center min-w-0 ${rowIndex % 2 === 0 ? 'bg-dashboard-zebra' : 'bg-white'} ${column.className || ''}`}
                        style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                      >
                        <div className="flex flex-col gap-1 w-full">
                          <Skeleton height={13} width="70%" radius="sm" />
                          <Skeleton height={11} width="50%" radius="sm" />
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))
              : data.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <div
                        key={colIndex}
                        // min-w-0: grid items default to min-width:auto, which lets
                        // long values push past their track and knock every column
                        // out of line — and stops `truncate` ever taking effect.
                        // min-h-16: the rows were sized for two-line cells. Most
                        // cells hold a single value now, so 80px left a band of
                        // empty space in every row; the cell still grows if a
                        // column does stack two lines.
                        className={`min-h-16 px-2.5 lg:px-3 py-3 border-b border-gray-200 flex items-center min-w-0 ${
                          rowIndex % 2 === 0 ? 'bg-dashboard-zebra' : 'bg-white'
                        } ${column.className || ''}`}
                        style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                      >
                        {column.render ? (
                          column.render(row, rowIndex)
                        ) : typeof column.accessor === 'function' ? (
                          column.accessor(row)
                        ) : (
                          <div className="text-dashboard-text text-xs font-medium leading-tight">
                            {column.accessor ? String(row[column.accessor]) : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DataTable
